import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors } from 'context';
import { Button, ButtonSizes } from 'atoms';

export interface ICareerStageCard {
  /** Emoji/glyph badge — no icon asset needed for the three stages. */
  icon: string;
  title: string;
  description: string;
  /** Progress caption ("3 / 8"), shown only on a live stage. */
  meta?: string;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Increment A ships only the Обучение stage; Резюме and Вакансии render as
   * visibly disabled "coming soon" placeholders and are deliberately not wired
   * to anything (spec 0018 §2 — those belong to increment B).
   */
  comingSoon?: boolean;
}

export const CareerStageCard = memo(
  ({ icon, title, description, meta, actionLabel, onAction, comingSoon }: ICareerStageCard) => {
    const { t } = useTranslation();

    return (
      <Card comingSoon={comingSoon}>
        <CardHeader>
          <StageIcon aria-hidden>{icon}</StageIcon>
          {comingSoon ? <SoonBadge>{t('careerCenter.stages.comingSoon')}</SoonBadge> : null}
        </CardHeader>

        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>

        {meta ? <CardMeta>{meta}</CardMeta> : null}

        {!comingSoon && actionLabel && onAction ? (
          <StageButton size={ButtonSizes.sm} onClick={onAction}>
            {actionLabel}
          </StageButton>
        ) : null}
      </Card>
    );
  },
);

const Card = styled.div<{ comingSoon?: boolean }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 24px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 16px;
  background-color: ${Colors.white};

  ${({ comingSoon }) =>
    comingSoon &&
    css`
      background-color: ${Colors.bg_gray};
      border-style: dashed;
      color: ${Colors.gray_dark};
    `}
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const StageIcon = styled.span`
  font-size: 28px;
  line-height: 1;
`;

const SoonBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  background-color: ${Colors.system_gray};
  color: ${Colors.gray_dark};
  white-space: nowrap;
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const CardTitle = styled.h3`
  margin: 0 0 8px 0;
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
`;

const CardDescription = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const CardMeta = styled.div`
  margin-top: 12px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const StageButton = styled(Button)`
  width: 100%;
  margin-top: auto;
  padding-top: 16px;

  ${tablet} {
    width: auto;
    align-self: flex-start;
    margin-top: 20px;
    padding-top: 0;
  }
`;
